import { useEffect, type ComponentType } from "react";
import { Briefcase, Check, ChevronDown, IdCard, MonitorCog, TrendingUp } from "lucide-react";
import { SkyMenu, type SkyMenuContent } from "@skypointcloud/skypoint-ui";
import { useNavigate } from "react-router";
import {
  PRODUCT_KEYS,
  PRODUCT_LABELS,
  PRODUCT_LANDING_PATH,
  PRODUCT_WORDMARK,
  type ProductKey,
} from "../../../shared/products/keys";
import { useEnabledProducts } from "../../hooks/useEnabledProducts";
import { useActiveProductState } from "../../hooks/useActiveProduct";
import { writeLastProduct } from "./lastProduct";
import { wordmarkVariantFor } from "./productTheme";

/**
 * The products the switcher offers (spec §6): those the instance has AND the user holds a role in,
 * in PRODUCT_KEYS order, and only when there are two or more. A single-product instance, a legacy
 * one, or a user holding one product gets none, and it never offers a product the user cannot use.
 * With the original two products this is exactly "the instance has both and the user holds a role
 * in each". A UI hint only: the server gates every product route itself.
 */
export function useSwitcherProducts(): readonly ProductKey[] {
  const { ready, has, holdsRole } = useEnabledProducts();
  if (!ready) return NONE;
  const held = PRODUCT_KEYS.filter((key) => has(key) && holdsRole(key));
  return held.length >= 2 ? held : NONE;
}
const NONE: readonly ProductKey[] = [];

/** Whether the switcher shows: see useSwitcherProducts. */
export function useProductSwitcherVisible(): boolean {
  return useSwitcherProducts().length > 0;
}

/**
 * How each product looks in the switcher: a tinted square holding its icon, in tokens that follow
 * light and dark. Hire is the brand's amber; the others take the status hues so no two read alike.
 */
const PRODUCT_LOOK: Record<
  ProductKey,
  { icon: ComponentType<{ className?: string }>; tint: string }
> = {
  skyhire: { icon: Briefcase, tint: "bg-accent/15 text-accent" },
  hris: { icon: IdCard, tint: "bg-info-subtle text-info" },
  skyperformance: { icon: TrendingUp, tint: "bg-success-subtle text-success" },
  skyperformance_it: { icon: MonitorCog, tint: "bg-secondary text-secondary-foreground" },
};

const Tile = ({ product }: { product: ProductKey }) => {
  const { icon: Icon, tint } = PRODUCT_LOOK[product];
  return (
    <span
      className={`inline-flex size-6 shrink-0 items-center justify-center rounded-md ${tint}`}
      aria-hidden
    >
      <Icon className="size-3.5" />
    </span>
  );
};

interface ProductSwitcherProps {
  /** "header" on the desktop header bar; "sheet" in the light mobile side menu. */
  variant?: "header" | "sheet";
  /** Called after a switch, so the mobile menu can close. */
  onSwitch?: () => void;
}

/**
 * The product dropdown (spec §6, D14): a pill showing the product the user is in, opening a menu
 * of the products they can switch to (skySupport's switcher). Rendered only when
 * useProductSwitcherVisible. The checked row is the active product (useActiveProduct), so on a
 * shared page such as Users it is the product the user is in. Choosing one saves the choice per
 * instance (localStorage, D8), then goes to that product's landing with the CURRENT ?instanceid=
 * kept (the catch-all redirect drops it).
 *
 * The instance comes from the URL, the same source HomeRoute reads the preference with, not from
 * useHeaderData: that hook fetches tenants and instances on every mount, and the header mounts this
 * twice (desktop bar and mobile menu).
 *
 * The desktop menu is the design system's SkyMenu (real menu roles, arrow keys, typeahead). The
 * mobile sheet is a plain list: there is no room for a popover inside the side menu, and it avoids
 * index.css painting a pressed button inside any role="group" (#1328), so no group role is used.
 */
export function ProductSwitcher({ variant = "header", onSwitch }: ProductSwitcherProps) {
  const offered = useSwitcherProducts();
  const visible = offered.length > 0;
  const offeredKey = offered.join(",");
  const { product: active, instanceId } = useActiveProductState();
  const navigate = useNavigate();

  // Warm the other wordmark so the header repaints from cache on a switch. Only for a user who can
  // switch: a hire-only header requests nothing new.
  useEffect(() => {
    if (!offeredKey) return;
    for (const key of offeredKey.split(",") as ProductKey[])
      new Image().src = PRODUCT_WORDMARK[key][wordmarkVariantFor(key)];
  }, [offeredKey]);

  if (!visible) return null;

  const go = (key: ProductKey) => {
    if (key === active) return;
    // Saved first, then navigated (skyAdvisor's ModeToggle): the shell repaints in the chosen
    // product at once, and a shared page opened afterwards keeps it.
    if (instanceId) writeLastProduct(instanceId, key);
    const query = instanceId ? `?instanceid=${encodeURIComponent(instanceId)}` : "";
    navigate(`${PRODUCT_LANDING_PATH[key]}${query}`);
    onSwitch?.();
  };

  if (variant === "sheet") {
    return (
      <nav
        aria-label="Product"
        className="flex flex-col gap-1"
      >
        {offered.map((key) => {
          const current = key === active;
          return (
            <button
              key={key}
              type="button"
              aria-current={current ? "true" : undefined}
              onClick={() => go(key)}
              className="flex h-10 w-full items-center gap-3 rounded-lg px-2 text-left text-sm font-semibold text-foreground hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-white/10"
            >
              <Tile product={key} />
              <span className="min-w-0 flex-1 truncate">{PRODUCT_LABELS[key]}</span>
              {current && (
                <Check
                  className="size-4 shrink-0"
                  aria-hidden
                />
              )}
            </button>
          );
        })}
      </nav>
    );
  }

  const items: SkyMenuContent[] = offered.map((key) => ({
    key,
    label: PRODUCT_LABELS[key],
    onClick: () => go(key),
    content: (
      <span className="flex w-full items-center gap-3">
        <Tile product={key} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{PRODUCT_LABELS[key]}</span>
        {key === active && (
          <Check
            className="size-4 shrink-0"
            aria-hidden
          />
        )}
      </span>
    ),
  }));

  return (
    <SkyMenu
      align="end"
      sideOffset={8}
      className="min-w-56"
      testId="product-switcher"
      items={items}
      trigger={
        <button
          type="button"
          aria-label={`Product: ${PRODUCT_LABELS[active]}`}
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-header-foreground/20 bg-header-foreground/5 px-2.5 text-header-foreground transition-colors hover:bg-header-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-header-foreground/80 focus-visible:ring-offset-2 focus-visible:ring-offset-header"
        >
          <Tile product={active} />
          <span className="whitespace-nowrap text-sm font-semibold">{PRODUCT_LABELS[active]}</span>
          <ChevronDown
            className="size-4 shrink-0"
            aria-hidden
          />
        </button>
      }
    />
  );
}

export default ProductSwitcher;
